import { defineStore } from 'pinia'

// Cookiebot's four consent categories. Sentry (error/perf monitoring) is classified under
// `statistics`, so the Sentry consent gate reads exactly that one (see src/sentry/init.ts).
export type ConsentCategory = 'necessary' | 'preferences' | 'statistics' | 'marketing'
export type ConsentCategories = Record<ConsentCategory, boolean>

const COOKIEBOT_CATEGORIES: ConsentCategory[] = ['necessary', 'preferences', 'statistics', 'marketing']

interface CookieConsentState {
	/** True once Cookiebot has loaded and reported a consent object at least once. */
	inited: boolean
	/** True once the user has actively accepted or declined the banner. */
	responded: boolean
	categories: ConsentCategories
}

/**
 * Mirror of Cookiebot's consent state, synced from the Cookiebot window events by
 * `setupCookieConsentSync()` (src/cookiebot.ts). The Sentry consent gate (src/sentry/init.ts)
 * subscribes to this store and initialises Sentry only once `categories.statistics === true` —
 * so there is no Sentry network traffic before statistics consent is active. Ported from
 * med-web-funnel `src/store/cookieConsent.js` (the analytics/Jet wiring is intentionally dropped).
 */
export const useCookieConsentStore = defineStore('cookieConsent', {
	state: (): CookieConsentState => ({
		inited: false,
		responded: false,
		categories: { necessary: false, preferences: false, statistics: false, marketing: false },
	}),

	getters: {
		/** Accepted category names, e.g. ['necessary', 'statistics']. */
		activeGroups(state): ConsentCategory[] {
			return COOKIEBOT_CATEGORIES.filter((cat) => state.categories[cat])
		},
		/** `(category) => boolean` — true when that category's consent is active. */
		isActiveConsentByName(state) {
			return (category: ConsentCategory): boolean => Boolean(state.categories[category])
		},
	},

	actions: {
		setInited(inited: boolean) {
			this.inited = inited
		},
		setResponded(responded: boolean) {
			this.responded = responded
		},
		setCategories(categories: Partial<ConsentCategories>) {
			this.categories = {
				necessary: Boolean(categories.necessary),
				preferences: Boolean(categories.preferences),
				statistics: Boolean(categories.statistics),
				marketing: Boolean(categories.marketing),
			}
		},
	},
})
